import { useEffect, useLayoutEffect, type ReactNode } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'

console.log('[App.tsx] Module loaded')

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation()

  console.log('[ScrollToTop] Route changed to:', pathname)

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}
import {
  WelcomePage,
  LandingPage,
  SurveyPage,
  PhotoUploadPage,
  TeaserPage,
  LoginPage,
  LoginWelcomePage,
  PaymentPage,
  PaymentSuccessPage,
  FullSurveyPage,
  CelebrationPage,
  ReportPage,
  NotFoundPage,
  TermsPage,
  PrivacyPage,
} from '@/pages'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ToastContainer } from '@/components/ui/Toast'
import { useAuthStore } from '@/store/authStore'

// Test component that throws an error (DEV ONLY - remove before production)
function TestErrorPage(): ReactNode {
  throw new Error('This is a test error to preview the error page')
}

function App() {
  const initAuth = useAuthStore((state) => state.initAuth)
  const navigate = useNavigate()
  const location = useLocation()

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)

  // Initialize auth on app load (checks redirect result, sets up listener)
  useEffect(() => {
    const init = async () => {
      console.log('[App] initAuth starting, current path:', location.pathname)
      const wasRedirect = await initAuth()
      console.log('[App] initAuth done, wasRedirect:', wasRedirect)
      // If we got a redirect result (mobile OAuth), navigate to payment
      // Verify user exists to avoid stale state issues
      if (wasRedirect) {
        const state = useAuthStore.getState()
        if (state.isAuthenticated && state.user) {
          console.log('[App] Redirect auth detected, navigating to /payment')
          navigate('/payment', { replace: true })
        } else {
          console.log('[App] Redirect detected but no valid user')
        }
        return
      }

      // Check if we need to poll for auth (mobile redirect fallback)
      const shouldCheckAuth = localStorage.getItem('k-skin-check-auth')
      if (shouldCheckAuth) {
        localStorage.removeItem('k-skin-check-auth')
        console.log('[App] Polling for auth state...')

        // Poll for auth state for up to 5 seconds
        let attempts = 0
        const pollInterval = setInterval(() => {
          attempts++
          const state = useAuthStore.getState()
          console.log('[App] Poll attempt', attempts, 'isAuthenticated:', state.isAuthenticated, 'user:', !!state.user)

          // Check both isAuthenticated AND user
          if (state.isAuthenticated && state.user) {
            clearInterval(pollInterval)
            console.log('[App] Auth detected via polling, navigating to /payment')
            navigate('/payment', { replace: true })
          } else if (attempts >= 10) {
            clearInterval(pollInterval)
            console.log('[App] Polling timeout, no auth detected')
          }
        }, 500)
      }
    }
    init()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Also watch for auth changes and redirect if on login page
  useEffect(() => {
    // Check both isAuthenticated AND user to avoid stale state
    if (isAuthenticated && user && location.pathname === '/login') {
      console.log('[App] Auth state changed while on login, navigating to /payment')
      navigate('/payment', { replace: true })
    }
  }, [isAuthenticated, user, location.pathname, navigate])

  return (
    <>
      <ScrollToTop />
      <ToastContainer />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<WelcomePage />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/survey" element={<SurveyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* DEV ONLY: Test error page - remove before production */}
        <Route path="/test-error" element={<TestErrorPage />} />
      <Route path="/photo" element={<PhotoUploadPage />} />
      <Route path="/teaser" element={<TeaserPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/welcome" element={<LoginWelcomePage />} />

      {/* Protected routes - require authentication */}
      {/* Temporarily removed ProtectedRoute for debugging */}
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />

      {/* Protected routes - require authentication + payment */}
      <Route
        path="/full-survey"
        element={
          <ProtectedRoute requirePayment>
            <FullSurveyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analysis-complete"
        element={
          <ProtectedRoute requirePayment>
            <CelebrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report"
        element={
          <ProtectedRoute requirePayment>
            <ReportPage />
          </ProtectedRoute>
        }
      />

      {/* 404 catch-all route */}
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  )
}

export default App
